/**
 * Ponte entre o editor do manual e a API.
 *
 * O documento inteiro viaja no campo `dados` (o mesmo objeto que o editor
 * manipula); tudo o mais nesta tela — código, status, revisão, progresso — é
 * projeção que o servidor mantém para o histórico poder listar sem baixar o
 * corpo dos manuais.
 */

import { apiService } from '@/services/api';
import { normalizarManual } from './manual-storage';
import type { ManualData } from './manual-types';

export type StatusManual = 'rascunho' | 'em_revisao' | 'concluido' | 'liberado';

export const STATUS_MANUAL: { valor: StatusManual; rotulo: string; descricao: string }[] = [
  { valor: 'rascunho', rotulo: 'Rascunho', descricao: 'Em preenchimento, ainda não revisado' },
  { valor: 'em_revisao', rotulo: 'Em revisão', descricao: 'Conteúdo fechado, aguardando conferência' },
  { valor: 'concluido', rotulo: 'Concluído', descricao: 'Revisado e pronto para emissão' },
  { valor: 'liberado', rotulo: 'Liberado', descricao: 'Emitido e entregue ao contratante' },
];

export function rotuloStatus(status: string): string {
  return STATUS_MANUAL.find((item) => item.valor === status)?.rotulo ?? status;
}

export type MotivoVersao = 'manual' | 'restauracao' | 'duplicacao' | 'status' | 'automatica';

export const ROTULO_MOTIVO: Record<MotivoVersao, string> = {
  manual: 'Versão registrada',
  restauracao: 'Antes de restaurar',
  duplicacao: 'Criado por cópia',
  status: 'Mudança de status',
  automatica: 'Automática',
};

export interface ManualResumo {
  id: string;
  projetoId: string;
  codigo: string;
  titulo: string;
  nomeMaquina: string;
  identificacao: string | null;
  modeloTipo: string | null;
  fabricante: string | null;
  contratante: string | null;
  setor: string | null;
  status: StatusManual;
  revisao: number;
  progresso: number;
  pendenciasCriticas: number;
  totalPaginas: number;
  capaUrl: string | null;
  isActive: boolean;
  criadoPorNome: string | null;
  atualizadoPorNome: string | null;
  origemManualId: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: { versoes: number };
}

export interface ManualVersaoResumo {
  id: string;
  manualId: string;
  numero: number;
  revisao: number;
  motivo: MotivoVersao;
  resumo: string | null;
  status: StatusManual;
  progresso: number;
  totalPaginas: number;
  autorNome: string | null;
  createdAt: string;
}

export interface ManualCompleto extends ManualResumo {
  dados: unknown;
  versoes?: ManualVersaoResumo[];
  projeto?: { id: string; nome: string; tipoProjeto: string };
}

export interface EstatisticasManuais {
  total: number;
  porStatus: Record<string, number>;
  progressoMedio: number;
  pendenciasCriticas: number;
}

export interface MetricasManual {
  progresso: number;
  pendenciasCriticas: number;
  totalPaginas: number;
}

export interface DiferencaVersao {
  campo: string;
  tipo: string;
  de: string;
  para: string;
}

/** Erro de API vira exceção: as telas tratam tudo num `catch` só. */
function conteudo<T>(resposta: { success: boolean; data?: T; message?: string }, acao: string): T {
  if (!resposta?.success) throw new Error(resposta?.message || `Não foi possível ${acao}.`);
  return resposta.data as T;
}

export async function listarManuais(filtros: {
  projetoId?: string;
  busca?: string;
  status?: string;
  incluirInativos?: boolean;
} = {}): Promise<ManualResumo[]> {
  const resposta = await apiService.listarManuais({ ...filtros, limite: 200 });
  return conteudo<ManualResumo[]>(resposta, 'carregar os manuais') ?? [];
}

export async function estatisticasManuais(projetoId?: string): Promise<EstatisticasManuais> {
  const resposta = await apiService.estatisticasManuais(projetoId);
  return conteudo<EstatisticasManuais>(resposta, 'carregar o resumo dos manuais');
}

export async function buscarManual(id: string): Promise<{ manual: ManualCompleto; dados: ManualData }> {
  const resposta = await apiService.buscarManual(id);
  const manual = conteudo<ManualCompleto>(resposta, 'abrir o manual');
  return { manual, dados: normalizarManual(manual.dados) };
}

export async function criarManual(params: {
  projetoId: string;
  dados: ManualData;
  titulo?: string;
  metricas?: Partial<MetricasManual>;
}): Promise<ManualResumo> {
  const resposta = await apiService.criarManual(params);
  return conteudo<ManualResumo>(resposta, 'criar o manual');
}

export async function salvarManual(id: string, params: {
  dados?: ManualData;
  titulo?: string;
  status?: StatusManual;
  metricas?: Partial<MetricasManual>;
}): Promise<ManualResumo> {
  const resposta = await apiService.atualizarManual(id, params);
  return conteudo<ManualResumo>(resposta, 'salvar o manual');
}

export async function duplicarManual(id: string, opcoes: {
  titulo?: string;
  limparEspecificos?: boolean;
} = {}): Promise<ManualResumo> {
  const resposta = await apiService.duplicarManual(id, opcoes);
  return conteudo<ManualResumo>(resposta, 'duplicar o manual');
}

export async function arquivarManual(id: string, definitivo = false): Promise<void> {
  const resposta = await apiService.removerManual(id, definitivo);
  if (!resposta?.success) throw new Error(resposta?.message || 'Não foi possível remover o manual.');
}

export async function reativarManual(id: string): Promise<ManualResumo> {
  const resposta = await apiService.reativarManual(id);
  return conteudo<ManualResumo>(resposta, 'reativar o manual');
}

export async function listarVersoes(id: string): Promise<ManualVersaoResumo[]> {
  const resposta = await apiService.listarVersoesManual(id);
  return conteudo<ManualVersaoResumo[]>(resposta, 'carregar o histórico') ?? [];
}

export async function buscarVersao(id: string, numero: number): Promise<{ versao: ManualVersaoResumo; dados: ManualData }> {
  const resposta = await apiService.buscarVersaoManual(id, numero);
  const versao = conteudo<ManualVersaoResumo & { dados: unknown }>(resposta, 'abrir a versão');
  return { versao, dados: normalizarManual(versao.dados) };
}

export async function congelarVersao(id: string, opcoes: {
  resumo?: string;
  bumpRevisao?: boolean;
  dados?: ManualData;
  motivo?: MotivoVersao;
}): Promise<ManualVersaoResumo> {
  const resposta = await apiService.congelarVersaoManual(id, opcoes);
  return conteudo<ManualVersaoResumo>(resposta, 'registrar a versão');
}

export async function restaurarVersao(id: string, numero: number): Promise<ManualResumo> {
  const resposta = await apiService.restaurarVersaoManual(id, numero);
  return conteudo<ManualResumo>(resposta, 'restaurar a versão');
}

export async function compararVersoes(id: string, de: number, para: number): Promise<{
  de: { numero: number; revisao: number; createdAt: string };
  para: { numero: number; revisao: number; createdAt: string };
  diferencas: DiferencaVersao[];
}> {
  const resposta = await apiService.compararVersoesManual(id, de, para);
  return conteudo(resposta, 'comparar as versões');
}

export async function enviarImagem(manualId: string, arquivo: File): Promise<string> {
  const resposta = await apiService.enviarImagemManual(manualId, arquivo);
  return conteudo<{ url: string }>(resposta, 'enviar a imagem').url;
}

/** `2026-08-23T12:04:00Z` → `23/08/2026 12:04`. */
export function formatarMomento(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '—';
  return `${data.toLocaleDateString('pt-BR')} ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}
